<script>
import Monitor from "@/services/Monitor";
import Customers from "@/services/Customers";
import Swal from "sweetalert2";

const PROPERTY_OPTIONS = ["visible", "required", "editable"];

export default {
  name: "ModalManageProfileDefinitions",
  props: {
    isMonitorMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      activeTab: 0,
      loading: false,
      saving: false,
      contextDefinitions: [],
      fieldDefinitions: [],
      showAddContext: false,
      showAddField: false,
      editingContext: null,
      newContext: {
        name: "",
        properties: ["visible", "required"],
      },
      newField: {
        name: "",
      },
      propertyOptions: PROPERTY_OPTIONS,
      contextFields: [
        { key: "name", label: "Context" },
        { key: "properties", label: "Properties" },
        { key: "active", label: "Active" },
        { key: "action", label: "Action" },
      ],
      fieldFields: [
        { key: "name", label: "Field" },
        { key: "active", label: "Active" },
        { key: "action", label: "Action" },
      ],
    };
  },
  computed: {
    api() {
      return this.isMonitorMode ? Monitor : Customers;
    },
    contextRows() {
      return this.contextDefinitions.map((context) => ({
        name: this.humanizeName(context.name),
        rawName: context.name,
        properties: Array.isArray(context.properties)
          ? context.properties
          : [],
        active: !!context.active,
      }));
    },
    fieldRows() {
      return this.fieldDefinitions.map((field) => ({
        name: this.humanizeName(field.name),
        rawName: field.name,
        active: !!field.active,
      }));
    },
  },
  methods: {
    humanizeName(name) {
      if (!name) {
        return "";
      }
      return String(name)
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    slugifyName(name) {
      return String(name || "")
        .toLowerCase()
        .trim()
        .replace(/[\s-]+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    closeModal() {
      this.showModal = false;
      this.resetAddForms();
    },
    resetAddForms() {
      this.showAddContext = false;
      this.showAddField = false;
      this.editingContext = null;
      this.newContext = {
        name: "",
        properties: ["visible", "required"],
      };
      this.newField = {
        name: "",
      };
    },
    loadDefinitions() {
      this.loading = true;

      Promise.all([
        this.api.getProfileContextDefinitions("all"),
        this.api.getProfileFieldsDefinitions("all"),
      ])
        .then(([contextResponse, fieldResponse]) => {
          this.contextDefinitions = contextResponse.data.data || [];
          this.fieldDefinitions = fieldResponse.data.data || [];
          this.$emit("refresh");
        })
        .catch((error) => {
          this.failedmsg(
            "Load failed",
            error.response?.data?.error || error.message
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onModalShown() {
      this.loadDefinitions();
    },
    toggleContextProperty(property) {
      const index = this.newContext.properties.indexOf(property);
      if (index === -1) {
        this.newContext.properties.push(property);
      } else {
        this.newContext.properties.splice(index, 1);
      }
    },
    startEditContext(row) {
      this.showAddContext = false;
      this.editingContext = {
        originalName: this.slugifyName(row.rawName || row.name),
        name: this.humanizeName(row.rawName || row.name),
        properties: [...row.properties],
      };
    },
    cancelEditContext() {
      this.editingContext = null;
    },
    toggleEditContextProperty(property) {
      if (!this.editingContext) {
        return;
      }
      const index = this.editingContext.properties.indexOf(property);
      if (index === -1) {
        this.editingContext.properties.push(property);
      } else {
        this.editingContext.properties.splice(index, 1);
      }
    },
    submitEditContext() {
      if (!this.editingContext) {
        return;
      }

      const name = this.editingContext.name.trim();
      if (!name) {
        this.failedmsg("Validation", "Context name is required");
        return;
      }
      if (this.editingContext.properties.length === 0) {
        this.failedmsg("Validation", "Select at least one property");
        return;
      }

      const slug = this.slugifyName(name);

      const payload = {
        name: this.editingContext.originalName,
        properties: [...this.editingContext.properties],
      };

      if (slug !== this.editingContext.originalName) {
        payload.new_name = slug;
      }

      this.saving = true;
      this.api
        .updateProfileContextDefinition(payload)
        .then((response) => {
          this.contextDefinitions = response.data.data || [];
          this.successmsg("Context updated");
          this.editingContext = null;
          this.$emit("refresh");
        })
        .catch((error) => {
          this.failedmsg(
            "Update failed",
            error.response?.data?.error || error.message
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    submitNewContext() {
      const name = this.newContext.name.trim();
      if (!name) {
        this.failedmsg("Validation", "Context name is required");
        return;
      }
      if (this.newContext.properties.length === 0) {
        this.failedmsg("Validation", "Select at least one property");
        return;
      }

      this.saving = true;
      this.api
        .createProfileContextDefinition({
          name,
          properties: this.newContext.properties,
        })
        .then((response) => {
          this.contextDefinitions = response.data.data || [];
          this.successmsg("Context definition added");
          this.resetAddForms();
          this.$emit("refresh");
        })
        .catch((error) => {
          this.failedmsg(
            "Add failed",
            error.response?.data?.error || error.message
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    submitNewField() {
      const name = this.newField.name.trim();
      if (!name) {
        this.failedmsg("Validation", "Field name is required");
        return;
      }

      this.saving = true;
      this.api
        .createProfileFieldsDefinition({
          name,
        })
        .then((response) => {
          this.fieldDefinitions = response.data.data || [];
          this.successmsg("Field definition added");
          this.resetAddForms();
          this.$emit("refresh");
        })
        .catch((error) => {
          this.failedmsg(
            "Add failed",
            error.response?.data?.error || error.message
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    toggleContextActive(row) {
      const active = !row.active;
      const action = active ? "activate" : "deactivate";

      Swal.fire({
        title: `${active ? "Activate" : "Deactivate"} context?`,
        text: `${this.humanizeName(row.rawName)} will be ${action}d.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (!result.value) {
          return;
        }

        this.saving = true;
        this.api
          .updateProfileContextDefinition({
            name: row.rawName,
            active,
          })
          .then((response) => {
            this.contextDefinitions = response.data.data || [];
            this.successmsg(`Context ${action}d`);
            this.$emit("refresh");
          })
          .catch((error) => {
            this.failedmsg(
              "Update failed",
              error.response?.data?.error || error.message
            );
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    toggleFieldActive(row) {
      const active = !row.active;
      const action = active ? "activate" : "deactivate";

      Swal.fire({
        title: `${active ? "Activate" : "Deactivate"} field?`,
        text: `${this.humanizeName(row.rawName)} will be ${action}d.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (!result.value) {
          return;
        }

        this.saving = true;
        this.api
          .updateProfileFieldsDefinition({
            name: row.rawName,
            active,
          })
          .then((response) => {
            this.fieldDefinitions = response.data.data || [];
            this.successmsg(`Field ${action}d`);
            this.$emit("refresh");
          })
          .catch((error) => {
            this.failedmsg(
              "Update failed",
              error.response?.data?.error || error.message
            );
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    deleteContext(row) {
      Swal.fire({
        title: "Delete context?",
        text: `${this.humanizeName(row.rawName)} and its profile field config will be permanently removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#74788d",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (!result.value) {
          return;
        }

        this.saving = true;
        this.api
          .deleteProfileContextDefinition(row.rawName)
          .then((response) => {
            this.contextDefinitions = response.data.data || [];
            this.successmsg("Context deleted");
            this.$emit("refresh");
          })
          .catch((error) => {
            this.failedmsg(
              "Delete failed",
              error.response?.data?.error || error.message
            );
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    deleteField(row) {
      Swal.fire({
        title: "Delete field?",
        text: `${this.humanizeName(row.rawName)} and its profile field config will be permanently removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f46a6a",
        cancelButtonColor: "#74788d",
        confirmButtonText: "Delete",
      }).then((result) => {
        if (!result.value) {
          return;
        }

        this.saving = true;
        this.api
          .deleteProfileFieldsDefinition(row.rawName)
          .then((response) => {
            this.fieldDefinitions = response.data.data || [];
            this.successmsg("Field deleted");
            this.$emit("refresh");
          })
          .catch((error) => {
            this.failedmsg(
              "Delete failed",
              error.response?.data?.error || error.message
            );
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    successmsg(message) {
      Swal.fire({
        title: "Success",
        text: message,
        icon: "success",
        confirmButtonColor: "#34c38f",
      });
    },
    failedmsg(title, message) {
      const text = Array.isArray(message) ? message.join(", ") : message;
      Swal.fire({
        title: title || "Error",
        text: text || "Something went wrong",
        icon: "error",
        confirmButtonColor: "#f46a6a",
      });
    },
  },
};
</script>

<template>
  <b-modal
    id="manage_profile_definitions"
    v-model="showModal"
    title="Manage Definitions and Fields"
    title-class="text-black font-18"
    size="lg"
    centered
    @shown="onModalShown"
    @hidden="resetAddForms"
  >
    <b-tabs v-model="activeTab" content-class="mt-3">
      <b-tab title="Contexts" active>
        <div class="d-flex justify-content-end mb-3">
          <button
            type="button"
            class="btn btn-sm btn-success"
            @click="showAddContext = !showAddContext; editingContext = null"
          >
            <i class="mdi mdi-plus me-1"></i>
            Add Context
          </button>
        </div>

        <div v-if="showAddContext" class="card bg-light mb-3">
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">Context name</label>
              <input
                v-model="newContext.name"
                type="text"
                class="form-control"
                placeholder="e.g. registration"
              />
              <small class="text-muted">Lowercase letters, numbers, underscores</small>
            </div>

            <div class="mb-3">
              <label class="form-label d-block">Properties</label>
              <div
                v-for="property in propertyOptions"
                :key="property"
                class="form-check form-check-inline"
              >
                <input
                  :id="`ctx-prop-${property}`"
                  class="form-check-input"
                  type="checkbox"
                  :checked="newContext.properties.includes(property)"
                  @change="toggleContextProperty(property)"
                />
                <label class="form-check-label" :for="`ctx-prop-${property}`">
                  {{ humanizeName(property) }}
                </label>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="saving"
              @click="submitNewContext"
            >
              <b-spinner v-if="saving" small class="me-1"></b-spinner>
              Save Context
            </button>
          </div>
        </div>

        <div v-if="editingContext" class="card border-primary mb-3">
          <div class="card-body">
            <h6 class="card-title mb-3">Edit Context</h6>
            <div class="mb-3">
              <label class="form-label">Context name</label>
              <input
                v-model="editingContext.name"
                type="text"
                class="form-control"
                placeholder="e.g. registration"
              />
              <small class="text-muted">Lowercase letters, numbers, underscores</small>
            </div>

            <div class="mb-3">
              <label class="form-label d-block">Properties</label>
              <div
                v-for="property in propertyOptions"
                :key="`edit-ctx-prop-${property}`"
                class="form-check form-check-inline"
              >
                <input
                  :id="`edit-ctx-prop-${property}`"
                  class="form-check-input"
                  type="checkbox"
                  :checked="editingContext.properties.includes(property)"
                  @change="toggleEditContextProperty(property)"
                />
                <label class="form-check-label" :for="`edit-ctx-prop-${property}`">
                  {{ humanizeName(property) }}
                </label>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-primary btn-sm me-2"
              :disabled="saving"
              @click="submitEditContext"
            >
              <b-spinner v-if="saving" small class="me-1"></b-spinner>
              Save Changes
            </button>
            <button
              type="button"
              class="btn btn-light btn-sm"
              :disabled="saving"
              @click="cancelEditContext"
            >
              Cancel
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <b-table
            :items="contextRows"
            :fields="contextFields"
            responsive="sm"
            small
            :busy="loading"
            show-empty
            empty-text="No context definitions found"
          >
            <template #cell(properties)="data">
              <span
                v-for="property in data.item.properties"
                :key="property"
                class="badge bg-light text-dark font-size-12 me-1"
              >
                {{ property }}
              </span>
              <span
                v-if="data.item.properties.length === 0"
                class="text-muted"
                >—</span
              >
            </template>

            <template #cell(active)="data">
              <span
                v-if="data.item.active"
                class="badge bg-success font-size-12"
                >Active</span
              >
              <span v-else class="badge bg-secondary font-size-12"
                >Inactive</span
              >
            </template>

            <template #cell(action)="data">
              <div class="d-flex flex-wrap gap-1">
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="data.item.active ? 'btn-outline-danger' : 'btn-outline-success'"
                    :style="!data.item.active ? { marginRight: '14px' } : {}"
                    :disabled="saving"
                    @click="toggleContextActive(data.item)"
                  >
                  {{ data.item.active ? "Deactivate" : "Activate" }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="saving"
                  @click="startEditContext(data.item)"
                >
                  Edit
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  :disabled="saving"
                  @click="deleteContext(data.item)"
                >
                  Delete
                </button>
              </div>
            </template>

            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ms-2">Loading...</strong>
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>

      <b-tab title="Fields">
        <div class="d-flex justify-content-end mb-3">
          <button
            type="button"
            class="btn btn-sm btn-success"
            @click="showAddField = !showAddField"
          >
            <i class="mdi mdi-plus me-1"></i>
            Add Field
          </button>
        </div>

        <div v-if="showAddField" class="card bg-light mb-3">
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">Field name</label>
              <input
                v-model="newField.name"
                type="text"
                class="form-control"
                placeholder="e.g. first_name"
              />
              <small class="text-muted">Lowercase letters, numbers, underscores</small>
            </div>

            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="saving"
              @click="submitNewField"
            >
              <b-spinner v-if="saving" small class="me-1"></b-spinner>
              Save Field
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <b-table
            :items="fieldRows"
            :fields="fieldFields"
            responsive="sm"
            small
            :busy="loading"
            show-empty
            empty-text="No field definitions found"
          >
            <template #cell(active)="data">
              <span
                v-if="data.item.active"
                class="badge bg-success font-size-12"
                >Active</span
              >
              <span v-else class="badge bg-secondary font-size-12"
                >Inactive</span
              >
            </template>

            <template #cell(action)="data">
              <div class="d-flex flex-wrap gap-1">
                <button
                  type="button"
                  class="btn btn-sm"
                  :class="data.item.active ? 'btn-outline-danger' : 'btn-outline-success'"
                  :disabled="saving"
                  @click="toggleFieldActive(data.item)"
                >
                  {{ data.item.active ? "Deactivate" : "Activate" }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  :disabled="saving"
                  @click="deleteField(data.item)"
                >
                  Delete
                </button>
              </div>
            </template>

            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong class="ms-2">Loading...</strong>
              </div>
            </template>
          </b-table>
        </div>
      </b-tab>
    </b-tabs>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" :disabled="loading" @click="loadDefinitions">
        <b-spinner v-show="loading" small class="me-1"></b-spinner>
        Refresh
      </b-button>
    </template>
  </b-modal>
</template>
